import { isNumber } from 'lodash';
import { Framework, type IHistoryItem } from '@/framework';
import type { ILayerProps } from '@/version-types';
import type { BackgroundLayer } from '.';
import { HistoryType, type IBatchUpdateBackgroundData, type IConvertBackgroundToImageData } from './type';

export class BackgroundHistory extends Framework {
    constructor(identity: string) {
        super(identity);
        this.registerConvertBackgroundToImageHistory();
        this.registerBatchUpdateBackgroundHistory();
    }

    private registerBatchUpdateBackgroundHistory() {
        this.framework.service.history.register(HistoryType.BATCH_UPDATE_BACKGROUND, {
            owner: 'layer/background',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { newLayers, oldLayers } = historyItem.data.data;
                if (isRedo) {
                    this.framework.service.frame.batchUpdateLayer(newLayers, true);
                }
                if (isUndo) {
                    this.framework.service.frame.batchUpdateLayer(oldLayers, true);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>) => {
                return {
                    message: [historyItem.data.data.newLayers],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>, isRedo) => {
                return {
                    message: [isRedo ? historyItem.data.data.newLayers : historyItem.data.data.oldLayers],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>, isRedo, isUndo, store) => {
                const { oldLayers } = historyItem.data.data;
                if (isUndo) {
                    const oldFlatLayers: ILayerProps[] = [];
                    const newFlatLayers: ILayerProps[] = [];
                    for (const layer of oldLayers) {
                        const latestLayer = this.framework.service.flatLayer.getLayer(layer.id, true);
                        if (latestLayer) {
                            oldFlatLayers.push(layer);
                            newFlatLayers.push(latestLayer);
                        }
                    }
                    historyItem.isDirty = !newFlatLayers.length;
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                        oldFlatLayers,
                        newFlatLayers,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    const data = historyItem.data.data;
                    const { oldFlatLayers, newFlatLayers } = beforeData;
                    data.oldLayers = oldFlatLayers;
                    data.newLayers = newFlatLayers;
                }
            },
            onMessage: (message: [ILayerProps[]]) => {
                this.framework.service.frame.batchUpdateLayer(message[0], true);
            },
        });
    }

    private registerConvertBackgroundToImageHistory() {
        this.framework.service.history.register(HistoryType.CONVERT_BACKGROUND_TO_IMAGE, {
            owner: 'layer/background',
            collaboration: 'supported',
            onRedoUndo: (
                historyItem: IHistoryItem<IConvertBackgroundToImageData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { backgroundLayer, imageLayer, bgOptions } = historyItem.data.data;
                if (isRedo) {
                    // 分离出的图片尚未恢复，通过仍存在的背景定位画布。
                    const originIndex = this.framework.service.frame.getLayerFrameId(backgroundLayer.id);
                    if (!isNumber(originIndex)) return;
                    const info = this.framework.service.frame.getFrameIdAndLayerIds(originIndex, true);
                    const originIds = info.layerIds;
                    originIds.splice(1, 0, imageLayer.id);
                    const currentLayer = this.framework.service.frame.getBackgroundLayer(originIndex) || undefined;
                    this.framework.service.registration
                        .getInstance<BackgroundLayer>('BackgroundLayer')
                        .update(bgOptions, false, '', currentLayer);
                    this.framework.service.frame.addOrUpdateLayer(imageLayer, originIndex);
                }
                if (isUndo) {
                    this.framework.service.frame.addOrUpdateLayer(backgroundLayer);
                    this.framework.service.frame.delLayer(imageLayer.id);
                    this.framework.service.frame.delActiveLayer();
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IConvertBackgroundToImageData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IConvertBackgroundToImageData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IConvertBackgroundToImageData>, isRedo, isUndo, store) => {
                if (isRedo) {
                    const layer = this.framework.service.flatLayer.getLayer(historyItem.data.data.imageLayer.id);
                    if (layer) {
                        historyItem.isDirty = true;
                    }
                }
            },
            onMessage: (message: [IConvertBackgroundToImageData], isRedo, isUndo, isWrite) => {
                const { backgroundLayer, imageLayer, bgOptions } = message[0];
                if (isRedo || isWrite) {
                    const originIndex = this.framework.service.frame.getLayerFrameId(backgroundLayer.id);
                    if (!isNumber(originIndex)) return;
                    const info = this.framework.service.frame.getFrameIdAndLayerIds(originIndex, true);
                    const originIds = info.layerIds;
                    originIds.splice(1, 0, imageLayer.id);
                    const currentLayer = this.framework.service.frame.getBackgroundLayer(originIndex) || undefined;
                    this.framework.service.registration
                        .getInstance<BackgroundLayer>('BackgroundLayer')
                        .update(bgOptions, false, '', currentLayer);
                    this.framework.service.frame.addOrUpdateLayer(imageLayer, originIndex);
                }
                if (isUndo) {
                    this.framework.service.frame.tryRemoveActiveLayer(imageLayer.id);
                    this.framework.service.frame.addOrUpdateLayer(backgroundLayer);
                    this.framework.service.frame.delLayer(imageLayer.id);
                }
            },
        });
    }
}
